
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useProfilePrivacy = () => {
  const { user } = useAuth();
  const [isPrivate, setIsPrivate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch current privacy setting
  useEffect(() => {
    const fetchPrivacySetting = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('is_private')
          .eq('id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 = row not found
          throw error;
        }

        if (data) {
          setIsPrivate(data.is_private || false);
        }
      } catch (error) {
        console.error('Error fetching privacy setting:', error);
        toast.error('Failed to load privacy settings');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrivacySetting();
  }, [user]);

  // Update privacy setting
  const updatePrivacySetting = async (newPrivacySetting: boolean) => {
    if (!user) return false;

    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          is_private: newPrivacySetting,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'id'
        });

      if (error) throw error;

      setIsPrivate(newPrivacySetting);
      toast.success(
        newPrivacySetting 
          ? 'Profile set to private. You will appear as anonymous to other users.'
          : 'Profile set to public. You will be visible to other users.'
      );
      return true;
    } catch (error: any) {
      console.error('Error updating privacy setting:', error);
      toast.error(error.message || 'Failed to update privacy setting');
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    isPrivate,
    isLoading,
    isUpdating,
    updatePrivacySetting
  };
};
